﻿/**
 * FeedSkeleton — v7 统一骨架屏系统
 *
 * 全局加载占位组件，覆盖：
 * - card:    信息流卡片（对谈/话题条目）
 * - list:    紧凑列表项（Club 频道 / 通知）
 * - detail:  详情页正文区
 * - profile: 个人主页头部（banner + 头像 + 信息 + 标签页）
 * - chat:    聊天/消息气泡
 * - stat:    统计数字行
 *
 * 所有变体共享 skeleton-shimmer 动画 + stagger 延迟。
 *
 * Usage:
 *   <FeedSkeleton variant="card" count={4} />
 *   <FeedSkeleton variant="profile" />
 *   <FeedSkeleton variant="chat" count={5} />
 *
 * @see index.css skeleton-shimmer / content-enter
 */

/* ── Shared primitive ── */
function Bone({ className = "" }: { className?: string }) {
  return <div className={`bg-slate-200 dark:bg-slate-700 rounded ${className}`} />;
}

function BoneThin({ className = "" }: { className?: string }) {
  return <div className={`bg-slate-100 dark:bg-slate-700/60 rounded ${className}`} />;
}

/* ── Variants ── */

function SkeletonCard() {
  return (
    <div className="bg-[var(--ig-surface)] rounded-xl border border-[var(--ig-border)] p-4 animate-pulse skeleton-shimmer">
      <div className="flex items-center gap-3 mb-3">
        <Bone className="w-8 h-8 rounded-full" />
        <div className="flex-1">
          <Bone className="h-3 w-24 mb-1.5" />
          <BoneThin className="h-2 w-16" />
        </div>
      </div>
      <Bone className="h-4 w-3/4 mb-2" />
      <BoneThin className="h-3 w-full mb-1.5" />
      <BoneThin className="h-3 w-5/6" />
      <div className="flex items-center gap-4 mt-3">
        <BoneThin className="h-3 w-12" />
        <BoneThin className="h-3 w-12" />
        <BoneThin className="h-3 w-12" />
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="bg-[var(--ig-surface)] rounded-xl border border-[var(--ig-border)] p-3 animate-pulse skeleton-shimmer flex items-center gap-3">
      <Bone className="w-10 h-10 rounded-lg shrink-0" />
      <div className="flex-1">
        <Bone className="h-3.5 w-2/3 mb-1.5" />
        <BoneThin className="h-2.5 w-1/3" />
      </div>
    </div>
  );
}

function SkeletonDetail() {
  return (
    <div className="animate-pulse space-y-4">
      <BoneThin className="h-3 w-1/3" />
      <div className="bg-[var(--ig-surface)] rounded-xl border border-[var(--ig-border)] p-5 space-y-4">
        <div className="flex items-center gap-3">
          <Bone className="w-10 h-10 rounded-full" />
          <div>
            <Bone className="h-3.5 w-28 mb-1.5" />
            <BoneThin className="h-2.5 w-20" />
          </div>
        </div>
        <Bone className="h-5 w-2/3" />
        <div className="space-y-2">
          <BoneThin className="h-3 w-full" />
          <BoneThin className="h-3 w-full" />
          <BoneThin className="h-3 w-4/5" />
          <BoneThin className="h-3 w-3/5" />
        </div>
      </div>
    </div>
  );
}

/** Profile header skeleton: banner + avatar + name + stats + tabs */
function SkeletonProfile() {
  return (
    <div className="animate-pulse">
      {/* Banner */}
      <div className="h-40 rounded-b-2xl bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 dark:from-slate-800 dark:via-slate-700 dark:to-slate-800 skeleton-shimmer" />
      {/* Header section */}
      <div className="px-4 -mt-10 mb-6 relative z-10">
        {/* Avatar */}
        <Bone className="w-20 h-20 rounded-full ring-4 ring-white dark:ring-slate-900" />
        <div className="mt-3 px-1">
          {/* Name + button */}
          <div className="flex items-center gap-3">
            <Bone className="h-6 w-32" />
            <div className="flex-1" />
            <Bone className="h-9 w-20 rounded-lg" />
          </div>
          {/* Bio */}
          <BoneThin className="h-3 w-3/4 mt-3" />
          <BoneThin className="h-3 w-1/2 mt-1.5" />
          {/* Stats */}
          <div className="flex items-center gap-4 mt-3">
            <BoneThin className="h-3 w-14" />
            <BoneThin className="h-3 w-14" />
            <BoneThin className="h-3 w-14" />
          </div>
        </div>
      </div>
      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-[var(--ig-border)] mb-4 px-4">
        {[48, 32, 32, 48].map((w, i) => (
          <div key={i} className="px-4 py-3">
            <BoneThin className={`h-3 ${w === 48 ? 'w-12' : 'w-8'}`} />
          </div>
        ))}
      </div>
      {/* Content cards */}
      <div className="px-4 space-y-3">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    </div>
  );
}

/** Chat message skeleton — alternating left/right bubbles */
function SkeletonChat() {
  return (
    <div className="animate-pulse space-y-4 px-4">
      {/* Incoming message (left) */}
      <div className="flex items-start gap-2">
        <Bone className="w-8 h-8 rounded-full shrink-0" />
        <div className="space-y-1.5 max-w-[70%]">
          <Bone className="h-4 w-48 rounded-xl" />
          <BoneThin className="h-4 w-32 rounded-xl" />
        </div>
      </div>
      {/* Outgoing message (right) */}
      <div className="flex items-start gap-2 justify-end">
        <div className="space-y-1.5 max-w-[70%]">
          <Bone className="h-4 w-40 rounded-xl ml-auto" />
        </div>
      </div>
      {/* Another incoming */}
      <div className="flex items-start gap-2">
        <Bone className="w-8 h-8 rounded-full shrink-0" />
        <div className="space-y-1.5 max-w-[70%]">
          <Bone className="h-4 w-56 rounded-xl" />
          <BoneThin className="h-4 w-44 rounded-xl" />
          <BoneThin className="h-4 w-20 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

/** Stat numbers skeleton — compact horizontal row */
function SkeletonStat() {
  return (
    <div className="animate-pulse flex items-center gap-6 py-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Bone className="h-4 w-6" />
          <BoneThin className="h-3 w-8" />
        </div>
      ))}
    </div>
  );
}

/* ── Public API ── */

type SkeletonVariant = "card" | "list" | "detail" | "profile" | "chat" | "stat";

interface FeedSkeletonProps {
  variant?: SkeletonVariant;
  count?: number;
}

const VARIANT_MAP: Record<SkeletonVariant, React.FC> = {
  card: SkeletonCard,
  list: SkeletonList,
  detail: SkeletonDetail,
  profile: SkeletonProfile,
  chat: SkeletonChat,
  stat: SkeletonStat,
};

export function FeedSkeleton({ variant = "card", count = 3 }: FeedSkeletonProps) {
  const Comp = VARIANT_MAP[variant] ?? SkeletonCard;
  // Profile / chat are self-contained — always render once
  if (variant === "profile" || variant === "chat") {
    return <Comp />;
  }
  return (
    <div className="space-y-3 stagger-list">
      {Array.from({ length: count }, (_, i) => <Comp key={i} />)}
    </div>
  );
}

/**
 * ContentTransition — Skeleton → Content crossfade wrapper.
 *
 * When `loading` is true, renders the skeleton.
 * When `loading` flips to false, children fade in via `content-enter` animation.
 *
 * Usage:
 *   <ContentTransition loading={isLoading} skeleton={<FeedSkeleton variant="card" count={3} />}>
 *     <ActualContent />
 *   </ContentTransition>
 */
export function ContentTransition({
  loading,
  skeleton,
  children,
}: {
  loading: boolean;
  skeleton: React.ReactNode;
  children: React.ReactNode;
}) {
  if (loading) return <>{skeleton}</>;
  return <div className="content-enter">{children}</div>;
}
